import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { EntityData, PartialEntityData } from '@adornis/baseql/entities/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Dependencies } from '@adornis/materialized-views/decorators/dependency.js';
import type { Aufenthalt, Weekday } from '@chancenwerk/enums/enums.js';
import { DateTime } from 'luxon';
import type { ChancenDynamicClassProduct } from './ChancenDynamicClassProduct.js';

export const PRODUCT_BLACKLIST = {
  _id: 1,
  ragYearInformation: 1,
  durchfuehrungsTage: {
    _class: 1,
    cid: 1, // Missing translations, so we just hide this field
  },
};

/**
 * @entity DurchfuehrungsTag
 */
@Entity()
export class DurchfuehrungsTag extends AdornisEntity {
  public static override _class = 'DurchfuehrungsTag';

  // ! We use this instead of _id, see resolveID for reasoning
  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public cid: Maybe<string>;

  @Field(() => String)
  public info: Maybe<string>;

  @Field(() => String)
  public wochentag: Maybe<Weekday>;

  @Field(() => DateTime)
  public startZeit: Maybe<DateTime>;

  @Field(() => DateTime)
  public endZeit: Maybe<DateTime>;

  @Field(() => [String], { default: v => v ?? [] })
  public locations!: string[];

  public override toString(): string {
    return this.wochentag ?? '';
  }

  public static override resolveID(entity: DurchfuehrungsTag): string {
    // ! We use DurchfuehrungsTag also on the student for the choosen intensive courses, which is used
    // ! in a print component. In this case we can't build a unique ID based on the fields of the entity.
    // ! This is a dirty hack, but it works for now.
    try {
      entity.locations;
    } catch (e) {
      return A.getGloballyUniqueID();
    }

    const id = (entity.wochentag ?? A.getGloballyUniqueID()) + '_' + (entity.locations ?? []).join('_');

    try {
      entity.cid;
    } catch (e) {
      return id;
    }

    return id + '_' + entity.cid;
  }

  public sameTime(starZeit: Maybe<DateTime>, endZeit: Maybe<DateTime>) {
    return (
      starZeit &&
      this.startZeit?.hasSame(starZeit, 'hour') &&
      this.startZeit.hasSame(starZeit, 'minute') &&
      endZeit &&
      this.endZeit?.hasSame(endZeit, 'hour') &&
      this.endZeit.hasSame(endZeit, 'minute')
    );
  }
}

export enum RAGGraduationTypes {
  HAUPTSCHULE = 'Hauptschulabschluss',
  MITTLERER_ABSCHLUSS = 'Mittlerer Schulabschluss',
  ABITUR = 'Abitur',
}

export enum RAGChancenAbschlussTypes {
  CRASHCOURSE = 'CHANCENabschluss-Crashkurs',
  INDIVIDUELL = 'individueller CHANCENabschluss-Kurs in der Schule',
  WOECHENTLICH = 'wöchentlicher CHANCENabschluss-Kurs',
}

/**
 * @entity RAGYearInformation
 */
@Entity()
export class RAGYearInformation extends AdornisEntity {
  public static override _class = 'RAGYearInformation';

  @Field(() => ID, { default: v => v ?? A.getGloballyUniqueID() })
  public _id: Maybe<string>;

  @Field(() => String)
  public numberOfCourses: Maybe<string>;

  @Field(() => String)
  public numberOfStudents: Maybe<string>;

  @Field(() => String)
  public numberOfBooklets: Maybe<string>;

  @Field(() => String)
  public numberOfTeacherAccounts: Maybe<string>;

  @Field(() => String)
  public year: Maybe<string>;

  @Field(() => String)
  public graduationType: Maybe<keyof RAGGraduationTypes>;

  @Field(() => String)
  public chancenAbschlussType: Maybe<keyof RAGChancenAbschlussTypes>;
}

/**
 * @entity Product
 */
@Entity()
export class Product extends MongoEntity {
  public static override _class = 'Product';
  public static override _collectionName = 'products';

  constructor(doc: Partial<EntityData<Product>>) {
    super(doc);

    // TODO better to override resolveID instead, but I cannot do a full testrun right now
    this._id ??= A.getGloballyUniqueID();
  }

  // static override resolveID(entity: Product) {
  //   return entity._id ?? (entity._id = A.getGloballyUniqueID());
  // }

  @Field(() => String, { default: v => v ?? A.getGloballyUniqueID() })
  public override _id!: string;

  @Dependencies<typeof Product>({
    classes: async () => [(await import('./ChancenDynamicClassProduct.js')).ChancenDynamicClassProduct],
    ids: async function (this: PartialEntityData<Product>) {
      const dynamicEntity = await import('./ChancenDynamicClassProduct.js');
      const col = await getRawCollection<ChancenDynamicClassProduct>(
        dynamicEntity.ChancenDynamicClassProduct._collectionName,
      );
      const dynamicClass = await col.findOne({ class: this._class });
      const id = dynamicClass?._id;
      return id ? [id] : [];
    },
  })
  @Field(() => String, {
    resolve: function (this: Product) {
      return async () => {
        if (this.name) return this.name;
        const dynamicEntity = await import('./ChancenDynamicClassProduct.js');
        const col = await getRawCollection<ChancenDynamicClassProduct>(
          dynamicEntity.ChancenDynamicClassProduct._collectionName,
        );
        const dynamicClass = await col.findOne({ class: this._class });
        return dynamicClass?.name;
      };
    },
  })
  public name: Maybe<string>;

  @Dependencies<typeof Product>({
    classes: async () => [(await import('./ChancenDynamicClassProduct.js')).ChancenDynamicClassProduct],
    ids: async function (this: PartialEntityData<Product>) {
      const dynamicEntity = await import('./ChancenDynamicClassProduct.js');
      const col = await getRawCollection<ChancenDynamicClassProduct>(
        dynamicEntity.ChancenDynamicClassProduct._collectionName,
      );
      const dynamicClass = await col.findOne({ class: this._class });
      return dynamicClass?._id ? [dynamicClass._id] : [];
    },
  })
  @Field(() => String, {
    resolve: function (this: Product) {
      return async () => {
        if (this.kuerzel) return this.kuerzel;
        const dynamicEntity = await import('./ChancenDynamicClassProduct.js');
        const col = await getRawCollection<ChancenDynamicClassProduct>(
          dynamicEntity.ChancenDynamicClassProduct._collectionName,
        );
        const dynamicClass = await col.findOne({ class: this._class });
        return dynamicClass?.kuerzel;
      };
    },
  })
  public kuerzel: Maybe<string>;

  /** @deprecated */
  public static capacitySelctionSet(): BaseQLSelectionSet<Product> {
    return {};
  }

  @Field(() => String)
  public betreuungsverhaeltnis: Maybe<string>;

  @Field(() => String)
  public aufenthalt: Maybe<Aufenthalt>;

  @Field(() => String)
  public betreuungsintensitaet: Maybe<string>;

  @Field(() => [RAGYearInformation], { default: v => v ?? [] })
  public ragYearInformation: Maybe<RAGYearInformation[]>;

  override toObject(filterResolved?: boolean, visited?: Set<AdornisEntity>): EntityData<this> & { _id: string } {
    const result = super.toObject(filterResolved, visited);
    if (filterResolved) {
      delete result.name;
      delete result.kuerzel;
    }
    return result;
  }

  override toString(): string {
    return this.kuerzel ?? '';
  }
}
